import * as React from "react";
import moment from "moment";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  OutlinedInput,
  Modal,
  Pagination,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import CallReceivedRoundedIcon from "@mui/icons-material/CallReceivedRounded";
import { connect } from "react-redux";
import {
  getWhatsappConversations,
  getWhatsappConversationMessages,
  manuallyAcceptInvitation,
  manuallyAcceptCampaignInvitation,
  sendWhatsappConversationMessage,
} from "../../app/store/actions/adminActions";
import {
  PlatformPage,
  PlatformPageHeader,
  PlatformCard,
  PlatformSearchBar,
  PlatformButton,
  PlatformLoadingBlock,
} from "../../app/components/platform";
import { platformColors, platformFonts } from "../../app/components/tickitPlatformTheme";

const TEMPLATE_CATEGORY_PREVIEW = {
  event_invitation: "Invitation",
  ticket_qr: "Ticket / QR",
  event_reminder: "Reminder",
  event_rejection: "Rejection reply",
  campaign_invitation: "Campaign invitation",
  campaign_plus_one: "Plus-one prompt",
  campaign_response: "Confirmation",
};

const getMessagePreview = (content) => {
  if (!content) return "No content";
  if (typeof content === "string") return content;
  if (content?.text?.body) return content.text.body;
  if (content?.body) return content.body;
  if (content?.button?.text) return content.button.text;
  if (content?.button?.payload) return content.button.payload;

  const cat = content?.category;
  if (cat && TEMPLATE_CATEGORY_PREVIEW[cat]) {
    const label = TEMPLATE_CATEGORY_PREVIEW[cat];
    if (content?.eventTitle) return `${label} · ${content.eventTitle}`;
    if (content?.campaignTitle) return `${label} · ${content.campaignTitle}`;
    return label;
  }

  if (content?.templateLabel) {
    return String(content.templateLabel).replace(/^Template:\s*/i, "").trim() || "WhatsApp message";
  }

  if (content?.type === "template" || content?.templateName) {
    return "WhatsApp message";
  }

  return "WhatsApp message";
};

export const WhatsappConversations = ({
  isLoading,
  whatsappConversations,
  whatsappConversationMessages,
  getWhatsappConversations,
  getWhatsappConversationMessages,
  manuallyAcceptInvitation,
  manuallyAcceptCampaignInvitation,
  sendWhatsappConversationMessage,
}) => {
  const [search, setSearch] = React.useState("");
  const [selectedConversation, setSelectedConversation] = React.useState(null);
  const [listPage, setListPage] = React.useState(1);
  const [messagePage, setMessagePage] = React.useState(1);
  const [processingAction, setProcessingAction] = React.useState("");
  const [infoModalMessage, setInfoModalMessage] = React.useState(null);
  const [composeMessage, setComposeMessage] = React.useState("");
  const [isSending, setIsSending] = React.useState(false);

  const listLimit = 20;
  const messageLimit = 80;

  const loadConversations = React.useCallback(
    (page = 1, searchValue = search) => {
      getWhatsappConversations({ page, limit: listLimit, search: searchValue });
    },
    [getWhatsappConversations, search]
  );

  const loadMessages = React.useCallback(
    (conversation, page = 1) => {
      if (!conversation?.number) return;
      getWhatsappConversationMessages({
        number: conversation.number,
        page,
        limit: messageLimit,
      });
    },
    [getWhatsappConversationMessages]
  );

  React.useEffect(() => {
    getWhatsappConversations({ page: 1, limit: listLimit, search: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    setListPage(1);
    loadConversations(1, search);
  };

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
    setMessagePage(1);
    loadMessages(conversation, 1);
  };

  const conversationDocs = whatsappConversations?.docs || [];
  const messagesDesc = whatsappConversationMessages?.docs || [];
  const messagesAsc = [...messagesDesc].reverse();

  React.useEffect(() => {
    if (!selectedConversation?.number) return;
    const refreshed = conversationDocs.find((c) => c.number === selectedConversation.number);
    if (refreshed) setSelectedConversation(refreshed);
  }, [conversationDocs, selectedConversation?.number]);

  const refreshSelectedData = () => {
    loadConversations(listPage, search);
    if (selectedConversation?.number) {
      loadMessages(selectedConversation, messagePage);
    }
  };

  const handleAcceptEventInvitation = async (invitationId) => {
    const key = `event-${invitationId}`;
    setProcessingAction(key);
    try {
      await manuallyAcceptInvitation({ invitationId });
      refreshSelectedData();
    } finally {
      setProcessingAction("");
    }
  };

  const handleAcceptCampaignInvitation = async (invitationId) => {
    const key = `campaign-${invitationId}`;
    setProcessingAction(key);
    try {
      await manuallyAcceptCampaignInvitation({ invitationId });
      refreshSelectedData();
    } finally {
      setProcessingAction("");
    }
  };

  const handleSendMessage = async () => {
    const body = composeMessage.trim();
    if (!selectedConversation?.number || !body) return;
    setIsSending(true);
    try {
      await sendWhatsappConversationMessage({
        number: selectedConversation.number,
        name: selectedConversation.name,
        body,
      });
      setComposeMessage("");
      refreshSelectedData();
    } finally {
      setIsSending(false);
    }
  };

  return (
    <PlatformPage>
      <PlatformPageHeader
        title="WhatsApp conversations"
        subtitle="Review complete admin conversations in one inbox view"
      />

      <PlatformCard title="Search" sx={{ mb: 2.5 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }}>
          <PlatformSearchBar
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or phone number…"
          />
          <PlatformButton variant="primary" onClick={handleSearch}>
            Search
          </PlatformButton>
          <PlatformButton variant="ghost" startIcon={<RefreshIcon />} onClick={() => loadConversations(listPage, search)}>
            Refresh
          </PlatformButton>
        </Stack>
      </PlatformCard>

      <PlatformCard flush sx={{ p: 0, overflow: "hidden" }}>
          <Paper elevation={0} sx={{ borderRadius: 0, border: "none", overflow: "hidden" }}>
            <Box sx={{ display: "flex", minHeight: { xs: "auto", md: 640 }, flexDirection: { xs: "column", md: "row" } }}>
              <Box sx={{ width: { xs: "100%", md: 360 }, borderRight: { md: "1px solid #ebe8f8" } }}>
                <Box sx={{ p: 1.5, bgcolor: "#f7f4ff", borderBottom: "1px solid #ebe8f8" }}>
                  <Typography sx={{ fontWeight: 700, color: "var(--primaryPurple)" }}>
                    Chats ({whatsappConversations?.totalDocs || 0})
                  </Typography>
                </Box>
                <Box sx={{ maxHeight: { xs: 280, md: 640 }, overflowY: "auto" }}>
                  {conversationDocs.map((conv) => {
                    const active = selectedConversation?.number === conv.number;
                    return (
                      <Box
                        key={conv.number}
                        onClick={() => handleSelectConversation(conv)}
                        sx={{
                          p: 1.5,
                          cursor: "pointer",
                          borderBottom: "1px solid var(--platform-border)",
                          bgcolor: active ? "rgba(90,30,200,0.10)" : "var(--platform-surface)",
                          "&:hover": { bgcolor: active ? "rgba(90,30,200,0.14)" : "var(--platform-surface-2)" },
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" spacing={1}>
                          <Typography fontWeight={700}>{conv.name || "Unknown contact"}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {conv.lastMessageAt ? moment(conv.lastMessageAt).format("DD MMM, HH:mm") : "-"}
                          </Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ color: "#56566a", mt: 0.5 }} noWrap>
                          {getMessagePreview(conv.lastMessage)}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center">
                          <Chip size="small" icon={<WhatsAppIcon />} label={conv.number} />
                          {conv.unreadCount > 0 && <Chip size="small" color="success" label={`${conv.unreadCount} new`} />}
                        </Stack>
                      </Box>
                    );
                  })}
                  {!isLoading && conversationDocs.length === 0 && (
                    <Box sx={{ p: 3, textAlign: "center" }}>
                      <Typography color="text.secondary">No conversations found.</Typography>
                    </Box>
                  )}
                </Box>
                <Divider />
                <Box sx={{ p: 1.5, display: "flex", justifyContent: "center" }}>
                  <Pagination
                    page={listPage}
                    count={whatsappConversations?.pages || 1}
                    onChange={(_, value) => {
                      setListPage(value);
                      loadConversations(value, search);
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ flex: 1, bgcolor: "var(--platform-surface-2)", display: "flex", flexDirection: "column" }}>
                <Box
                  sx={{
                    p: 1.5,
                    borderBottom: "1px solid var(--platform-border)",
                    bgcolor: "linear-gradient(90deg, #1E0550 0%, #46109B 48%, #5A1EC8 100%)",
                    background: "linear-gradient(90deg, #1E0550 0%, #46109B 48%, #5A1EC8 100%)",
                    color: "#fff",
                  }}
                >
                  <Typography fontWeight={700}>
                    {selectedConversation?.name || "Select a conversation"}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {selectedConversation?.number || "Choose a chat from the left panel"}
                  </Typography>
                </Box>

                {!!selectedConversation && (
                  <Box sx={{ p: 1.25, bgcolor: "var(--platform-surface)", borderBottom: "1px solid var(--platform-border)" }}>
                    <Stack spacing={1.25}>
                      <Typography variant="subtitle2" sx={{ color: "var(--primaryPurple)", fontWeight: 700 }}>
                        Related Event Invitations
                      </Typography>
                      {(selectedConversation.relatedEventInvitations || []).length === 0 ? (
                        <Typography variant="caption" color="text.secondary">
                          No related event invitations for this phone number.
                        </Typography>
                      ) : (
                        (selectedConversation.relatedEventInvitations || []).map((inv) => (
                          <Stack
                            key={inv.invitationId}
                            direction={{ xs: "column", md: "row" }}
                            justifyContent="space-between"
                            spacing={1}
                            sx={{ p: 1, border: "1px solid var(--platform-border)", borderRadius: 1.5 }}
                          >
                            <Box>
                              <Typography variant="body2" fontWeight={700}>
                                {inv.eventTitle || "Event"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {inv.category || "N/A"} - {inv.status}
                              </Typography>
                            </Box>
                            {inv.canAccept && (
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={processingAction === `event-${inv.invitationId}`}
                                onClick={() => handleAcceptEventInvitation(inv.invitationId)}
                                sx={{ textTransform: "none" }}
                              >
                                {processingAction === `event-${inv.invitationId}`
                                  ? "Processing..."
                                  : inv.status === "Accepted"
                                    ? "Resend QR"
                                    : "Accept"}
                              </Button>
                            )}
                            {!inv.canAccept &&
                              inv.eventStarted &&
                              (inv.status === "Unseen" || inv.status === "Accepted") && (
                                <Typography variant="caption" color="text.secondary">
                                  Event has started
                                </Typography>
                              )}
                          </Stack>
                        ))
                      )}

                      <Typography variant="subtitle2" sx={{ color: "var(--primaryPurple)", fontWeight: 700, mt: 0.75 }}>
                        Related Campaign Invitations
                      </Typography>
                      {(selectedConversation.relatedCampaignInvitations || []).length === 0 ? (
                        <Typography variant="caption" color="text.secondary">
                          No related campaign invitations for this phone number.
                        </Typography>
                      ) : (
                        (selectedConversation.relatedCampaignInvitations || []).map((inv) => (
                          <Stack
                            key={inv.invitationId}
                            direction={{ xs: "column", md: "row" }}
                            justifyContent="space-between"
                            spacing={1}
                            sx={{ p: 1, border: "1px solid var(--platform-border)", borderRadius: 1.5 }}
                          >
                            <Box>
                              <Typography variant="body2" fontWeight={700}>
                                {inv.campaignTitle || "Campaign"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {inv.status} - Plus one: {inv.plusOne ? "yes" : "no"}
                              </Typography>
                            </Box>
                            {inv.canAccept && (
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={processingAction === `campaign-${inv.invitationId}`}
                                onClick={() => handleAcceptCampaignInvitation(inv.invitationId)}
                                sx={{ textTransform: "none" }}
                              >
                                {processingAction === `campaign-${inv.invitationId}`
                                  ? "Processing..."
                                  : inv.status === "accepted"
                                    ? "Resend Prompt"
                                    : "Accept"}
                              </Button>
                            )}
                          </Stack>
                        ))
                      )}
                    </Stack>
                  </Box>
                )}

                <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
                  {isLoading && (
                    <Box sx={{ py: 8, textAlign: "center" }}>
                      <CircularProgress />
                    </Box>
                  )}

                  {!isLoading && !selectedConversation && (
                    <Box sx={{ py: 12, textAlign: "center" }}>
                      <Typography variant="h6" color="text.secondary">
                        Select a chat to view full conversation
                      </Typography>
                    </Box>
                  )}

                  {!isLoading &&
                    selectedConversation &&
                    messagesAsc.map((message) => {
                      const isOutbound = message.direction === "outbound";
                      return (
                        <Box
                          key={message._id}
                          sx={{
                            display: "flex",
                            justifyContent: isOutbound ? "flex-end" : "flex-start",
                            mb: 1.5,
                          }}
                        >
                          <Paper
                            sx={{
                              maxWidth: "min(680px, 92%)",
                              p: 1.25,
                              borderRadius: 2,
                              bgcolor: isOutbound ? "rgba(220, 248, 198, 0.85)" : "var(--platform-surface)",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                            }}
                          >
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.4 }}>
                              {isOutbound ? (
                                <SendRoundedIcon sx={{ fontSize: 14, color: "var(--primaryPurple)" }} />
                              ) : (
                                <CallReceivedRoundedIcon sx={{ fontSize: 14, color: "#16a34a" }} />
                              )}
                              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                {isOutbound ? "Sent by Tickit" : "Customer"}
                              </Typography>
                            </Stack>
                            <Typography variant="body2">{getMessagePreview(message.content)}</Typography>
                            {!!message?.messageInfo && (
                              <Box sx={{ mt: 0.4 }}>
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => setInfoModalMessage(message)}
                                  sx={{ textTransform: "none", p: 0, minWidth: "auto", fontSize: 12 }}
                                >
                                  View info
                                </Button>
                              </Box>
                            )}
                            <Typography
                              variant="caption"
                              sx={{ display: "block", textAlign: "right", mt: 0.5, color: "#666" }}
                            >
                              {moment(message.createdAt).format("DD MMM YYYY, HH:mm")}
                              {message.status ? ` - ${message.status}` : ""}
                            </Typography>
                          </Paper>
                        </Box>
                      );
                    })}
                </Box>

                {selectedConversation && (
                  <Box sx={{ p: 1.25, borderTop: "1px solid var(--platform-border)", bgcolor: "var(--platform-surface)" }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {whatsappConversationMessages?.totalDocs || 0} messages
                      </Typography>
                      <Pagination
                        size="small"
                        page={messagePage}
                        count={whatsappConversationMessages?.pages || 1}
                        onChange={(_, value) => {
                          setMessagePage(value);
                          loadMessages(selectedConversation, value);
                        }}
                      />
                    </Stack>
                    <Stack spacing={0.75}>
                      <Stack direction="row" spacing={1}>
                        <OutlinedInput
                          fullWidth
                          value={composeMessage}
                          onChange={(e) => setComposeMessage(e.target.value)}
                          placeholder="Type a WhatsApp message (free text only works within 24h of their last message)..."
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              handleSendMessage();
                            }
                          }}
                        />
                        <Button
                          variant="contained"
                          onClick={handleSendMessage}
                          disabled={isSending || !composeMessage.trim()}
                          sx={{ textTransform: "none", minWidth: 110 }}
                        >
                          {isSending ? "Sending..." : "Send"}
                        </Button>
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>
                        Outside the 24-hour care window, Meta rejects free-form sends (error “Re-engagement message”). Use an approved template from Business Manager or have the customer message you first.
                      </Typography>
                    </Stack>
                  </Box>
                )}
              </Box>
            </Box>
          </Paper>
      </PlatformCard>

      <Modal
        open={!!infoModalMessage}
        onClose={() => setInfoModalMessage(null)}
        sx={{
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(0,0,0,0.4)",
            backdropFilter: "blur(2px)",
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "92vw", sm: 480 },
            bgcolor: "var(--platform-surface)",
            borderRadius: 2,
            p: 2.2,
            outline: "none",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: "var(--primaryPurple)", mb: 1 }}>
            Message Details
          </Typography>
          <Divider sx={{ mb: 1.5 }} />
          <Stack spacing={0.8}>
            <Typography variant="body2">
              <strong>Preview:</strong> {getMessagePreview(infoModalMessage?.content)}
            </Typography>
            <Typography variant="body2">
              <strong>Message ID:</strong> {infoModalMessage?.messageId || "N/A"}
            </Typography>
            <Typography variant="body2">
              <strong>Source:</strong> {infoModalMessage?.sourceField || "WPMessage"}
            </Typography>
            {!!infoModalMessage?.messageInfo && (
              <Typography variant="body2" sx={{ color: "#b42318" }}>
                <strong>Info:</strong> {infoModalMessage?.messageInfo}
              </Typography>
            )}
            {!!infoModalMessage?.error &&
              infoModalMessage?.error !== infoModalMessage?.messageInfo &&
              !String(infoModalMessage?.messageInfo || "").includes(String(infoModalMessage?.error || "")) && (
                <Typography variant="body2" sx={{ color: "#b42318" }}>
                  <strong>Error:</strong> {infoModalMessage?.error}
                </Typography>
              )}
            <Typography variant="body2" color="text.secondary">
              <strong>Sent at:</strong> {moment(infoModalMessage?.createdAt).format("DD MMM YYYY, HH:mm:ss")}
            </Typography>
          </Stack>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button onClick={() => setInfoModalMessage(null)} variant="contained" sx={{ textTransform: "none" }}>
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    </PlatformPage>
  );
};

const mapStateToProps = (state) => ({
  isLoading: state?.records?.isLoading,
  whatsappConversations: state?.records?.results?.whatsappConversations,
  whatsappConversationMessages: state?.records?.results?.whatsappConversationMessages,
});

export default connect(mapStateToProps, {
  getWhatsappConversations,
  getWhatsappConversationMessages,
  manuallyAcceptInvitation,
  manuallyAcceptCampaignInvitation,
  sendWhatsappConversationMessage,
})(WhatsappConversations);
